import { useQuery, useQueryClient } from '@tanstack/react-query';

import { customAxios } from 'lib/axios';
import { Project } from 'types';
import { refetchUserOnError } from 'utils/refetchUserOnError';

export type GetProjectsResponse = {
  projects: Project[];
  createdIssuesLast7Days: number[];
  completedIssuesLast7Days: number[];
};

const getProjects = async (
  orgId: string | undefined,
  includeArchived = false
): Promise<GetProjectsResponse> => {
  if (typeof orgId === 'undefined') {
    return Promise.reject(new Error('Invalid orgId'));
  }

  const { data } = await customAxios.get<GetProjectsResponse>(
    `/org/${orgId}/projects`,
    includeArchived ? { params: { includeArchived: true } } : undefined
  );
  return data;
};

export const useGetProjects = (
  orgId: string | undefined,
  includeArchived = false
) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['org', orgId, 'projects', includeArchived],
    queryFn: async () => {
      const data = await getProjects(orgId, includeArchived);

      data.projects.forEach((project) => {
        queryClient.setQueryData(['projects', project._id], project);
      });

      return data;
    },
    enabled: Boolean(orgId),
    onError: (err) => {
      refetchUserOnError(err, queryClient);
    },
  });
};
